<script lang="ts">
  import type {
    ExperienceLevel,
    FinanceRole,
    StudentCalculatorData,
  } from "$lib/types/landing"
  import { trackCalculatorUsage } from "../utils/analytics.js"
  import { createScrollAnimation } from "../utils/animations.js"
  import { fly, scale, slide } from "svelte/transition"
  import { quintOut, elasticOut } from "svelte/easing"
  import { onMount } from "svelte"

  interface Props {
    class?: string
    onCalculationComplete?: (data: StudentCalculatorData) => void
  }

  let { class: className = "", onCalculationComplete }: Props = $props()

  // Calculator state
  let experience = $state<ExperienceLevel>("new-grad")
  let role = $state<FinanceRole>("investment-banking")
  let currentStep = $state(1)
  let isVisible = $state(false)
  let containerRef: HTMLDivElement

  // Experience levels and roles data
  const experienceLevels = [
    { value: "new-grad" as const, label: "New Grad / 0–2 years", icon: "🎓" },
    { value: "2-4-years" as const, label: "2–4 years", icon: "💼" },
    { value: "4-plus-years" as const, label: "4+ years", icon: "🏆" },
  ]

  const roles = [
    {
      value: "investment-banking" as const,
      label: "Investment Banking",
      icon: "🏦",
    },
    { value: "private-equity" as const, label: "Private Equity", icon: "💰" },
    { value: "hedge-funds" as const, label: "Hedge Funds", icon: "📈" },
    { value: "equity-research" as const, label: "Equity Research", icon: "🔍" },
    { value: "venture-capital" as const, label: "Venture Capital", icon: "🚀" },
  ]

  // Salary data matrix
  const salaryData = {
    "new-grad": {
      "investment-banking": 110000,
      "private-equity": 120000,
      "hedge-funds": 130000,
      "equity-research": 100000,
      "venture-capital": 115000,
    },
    "2-4-years": {
      "investment-banking": 150000,
      "private-equity": 180000,
      "hedge-funds": 200000,
      "equity-research": 140000,
      "venture-capital": 160000,
    },
    "4-plus-years": {
      "investment-banking": 200000,
      "private-equity": 250000,
      "hedge-funds": 300000,
      "equity-research": 180000,
      "venture-capital": 220000,
    },
  }

  // Calculated values
  const salary = $derived(salaryData[experience][role])
  const monthlyInvestment = 99
  const annualCost = monthlyInvestment * 12
  const roiMultiplier = $derived(Math.round(salary / annualCost))
  const netReturn = $derived(salary - annualCost)

  // Progress calculation
  const progressPercentage = $derived((currentStep / 3) * 100)

  onMount(() => {
    if (containerRef) {
      const cleanup = createScrollAnimation(containerRef, {
        threshold: 0.2,
        rootMargin: "0px 0px -100px 0px",
      })

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              isVisible = true
            }
          })
        },
        { threshold: 0.2 },
      )

      observer.observe(containerRef)

      return () => {
        cleanup()
        observer.disconnect()
      }
    }
  })

  function handleExperienceChange(event: Event) {
    const target = event.target as HTMLSelectElement
    experience = target.value as ExperienceLevel
    if (currentStep === 1) {
      setTimeout(() => {
        currentStep = 2
      }, 300)
    }
    trackUsage()
  }

  function handleRoleChange(event: Event) {
    const target = event.target as HTMLSelectElement
    role = target.value as FinanceRole
    if (currentStep === 2) {
      setTimeout(() => {
        currentStep = 3
      }, 300)
    }
    trackUsage()
  }

  function trackUsage() {
    trackCalculatorUsage("students", {
      calculatorType: "student-roi",
      experience,
      role,
      salary,
    })

    // Call completion callback if provided
    if (onCalculationComplete) {
      onCalculationComplete({
        experience,
        role,
        salary,
        investment: annualCost,
        roi: roiMultiplier,
      })
    }
  }

  function handleGetStarted() {
    window.location.href = "/pricing"
  }
</script>

<div
  bind:this={containerRef}
  class="py-16 bg-gradient-to-br from-base-100 to-base-200 {className}"
>
  <div class="container mx-auto px-6">
    {#if isVisible}
      <div
        class="text-center mb-12"
        in:fly={{ y: 50, duration: 800, easing: quintOut }}
      >
        <h2 class="text-4xl font-bold mb-4 gradient-text">
          Calculate Your Return on Investment
        </h2>
        <p class="text-xl text-base-content/70 max-w-2xl mx-auto">
          See how Ace The Round can boost your finance career earnings
        </p>
      </div>

      <div
        class="card w-full max-w-4xl mx-auto bg-white border border-primary/20 shadow-xl"
        in:scale={{ duration: 600, delay: 200, easing: quintOut }}
      >
        <div class="card-header pb-6 p-8">
          <!-- Progress Bar -->
          <div class="w-full bg-base-200 rounded-full h-2 mb-6">
            <div
              class="bg-gradient-to-r from-primary to-accent h-2 rounded-full transition-all duration-500 ease-out"
              style="width: {progressPercentage}%"
            ></div>
          </div>

          <h2 class="card-title text-3xl font-bold text-center mb-2">
            ROI Calculator
          </h2>
          <p class="text-center text-lg text-base-content/70">
            Step-by-step breakdown of your investment return
          </p>
        </div>

        <div class="card-body space-y-8">
          <!-- Step 1: Experience Level -->
          <div class="space-y-4">
            <div class="flex items-center gap-3 mb-4">
              <div
                class="w-8 h-8 rounded-full bg-primary text-primary-content flex items-center justify-center font-bold"
              >
                1
              </div>
              <label for="experience-select" class="text-lg font-semibold">
                What's your experience level?
              </label>
            </div>

            <select
              id="experience-select"
              bind:value={experience}
              onchange={handleExperienceChange}
              class="select select-bordered w-full bg-white border-2 hover:border-primary/50 transition-colors text-lg"
            >
              {#each experienceLevels as level}
                <option value={level.value}>
                  {level.icon}
                  {level.label}
                </option>
              {/each}
            </select>
          </div>

          <!-- Step 2: Role Selection -->
          {#if currentStep >= 2}
            <div
              class="space-y-4"
              in:slide={{ duration: 500, easing: quintOut }}
            >
              <div class="flex items-center gap-3 mb-4">
                <div
                  class="w-8 h-8 rounded-full bg-primary text-primary-content flex items-center justify-center font-bold"
                >
                  2
                </div>
                <label for="role-select" class="text-lg font-semibold">
                  What's your target role?
                </label>
              </div>

              <select
                id="role-select"
                bind:value={role}
                onchange={handleRoleChange}
                class="select select-bordered w-full bg-white border-2 hover:border-primary/50 transition-colors text-lg"
              >
                {#each roles as r}
                  <option value={r.value}>
                    {r.icon}
                    {r.label}
                  </option>
                {/each}
              </select>
            </div>
          {/if}

          <!-- Step 3: Results -->
          {#if currentStep >= 3}
            <div
              class="space-y-6"
              in:slide={{ duration: 500, delay: 200, easing: quintOut }}
            >
              <div class="flex items-center gap-3 mb-4">
                <div
                  class="w-8 h-8 rounded-full bg-accent text-accent-content flex items-center justify-center font-bold"
                >
                  3
                </div>
                <div class="text-lg font-semibold">Your Investment Results</div>
              </div>

              <!-- Estimated Salary -->
              <div
                class="p-6 bg-primary/10 rounded-lg border border-primary/20"
                in:fly={{ y: 30, duration: 600, delay: 300, easing: quintOut }}
              >
                <p class="text-lg font-semibold text-center mb-2">
                  Estimated First-Year Salary
                </p>
                <p class="text-4xl font-bold text-primary text-center">
                  ${salary.toLocaleString()}
                </p>
              </div>

              <!-- Calculation Breakdown -->
              <div
                class="bg-base-100 p-6 rounded-lg border"
                in:fly={{ y: 30, duration: 600, delay: 500, easing: quintOut }}
              >
                <h3
                  class="text-xl font-bold text-primary mb-4 flex items-center gap-2"
                >
                  📊 Calculation Breakdown
                </h3>
                <div class="space-y-4">
                  <div
                    class="flex justify-between items-center p-3 bg-white rounded border"
                  >
                    <span>Monthly Investment</span>
                    <span class="font-semibold">${monthlyInvestment}</span>
                  </div>
                  <div
                    class="flex justify-between items-center p-3 bg-white rounded border"
                  >
                    <span>Annual Cost</span>
                    <span class="font-semibold"
                      >${annualCost.toLocaleString()}</span
                    >
                  </div>
                  <div
                    class="flex justify-between items-center p-3 bg-primary/10 rounded border border-primary/20"
                  >
                    <span class="font-semibold">ROI Multiplier</span>
                    <span class="text-2xl font-bold text-primary"
                      >{roiMultiplier}x</span
                    >
                  </div>
                </div>
              </div>

              <!-- Final ROI Display -->
              <div
                class="bg-gradient-to-r from-primary to-accent p-8 rounded-lg text-white text-center"
                in:scale={{ duration: 600, delay: 700, easing: elasticOut }}
              >
                <p class="text-3xl font-bold mb-3">
                  🎯 ROI: {roiMultiplier}x Return
                </p>
                <p class="text-xl mb-2">
                  That's a <span class="font-bold underline"
                    >${netReturn.toLocaleString()}</span
                  > return
                </p>
                <p class="text-lg opacity-90">
                  on your ${annualCost.toLocaleString()} annual investment!
                </p>
              </div>

              <!-- CTA Button -->
              <div
                class="text-center pt-4"
                in:fly={{ y: 30, duration: 600, delay: 900, easing: quintOut }}
              >
                <button
                  onclick={handleGetStarted}
                  class="btn btn-accent btn-lg font-bold text-lg px-12 py-4 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  🚀 Get Started For Less Than a Coffee a Day!
                </button>
                <p class="text-sm text-base-content/60 mt-3">
                  Join 5,000+ finance professionals who advanced their careers
                </p>
              </div>
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</div>
